/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { Text, TouchableOpacity, View, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { colors } from '../../../shared/theme/colors';
import { styles } from '../ReportsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors';

export type ReportsMetricTileLayout =
  | 'default'
  | 'wide'
  | 'adaptiveWide'
  | 'personal'
  | 'personalWide';

export type ReportsMetricTileValueTone =
  | 'default'
  | 'emphasis'
  | 'positive'
  | 'negative'
  | 'warning';

export type ReportsMetricTileProps = {
  label?: string;
  labelVariant?: 'kpi' | 'breakdown';
  value: string;
  sub?: string;
  subAccent?: boolean;
  subWarning?: boolean;
  valueTone?: ReportsMetricTileValueTone;
  valueEmphasis?: boolean;
  warning?: boolean;
  layout?: ReportsMetricTileLayout;
  onPress?: () => void;
  disabled?: boolean;
  accessibilityLabel?: string;
  valueNumberOfLines?: number;
  subNumberOfLines?: number;
  style?: StyleProp<ViewStyle>;
};

function layoutStyle(layout: ReportsMetricTileLayout): ViewStyle | undefined {
  switch (layout) {
    case 'wide':
      return styles.reportsMetricTileWide;
    case 'adaptiveWide':
      return styles.reportsMetricTileAdaptiveWide;
    case 'personal':
      return styles.reportsMetricTilePersonal;
    case 'personalWide':
      return styles.reportsMetricTilePersonalWide;
    default:
      return undefined;
  }
}

function valueToneStyle(
  tone: ReportsMetricTileValueTone,
  personal: boolean,
  emphasis: boolean,
): TextStyle[] {
  const base = personal ? styles.personalKpiTileValue : styles.ventasIndicatorValue;
  const stylesList: TextStyle[] = [base];
  if (emphasis) {
    stylesList.push(styles.ventasIndicatorValueEmphasis);
  }
  switch (tone) {
    case 'emphasis':
      if (!emphasis) stylesList.push(styles.ventasIndicatorValueEmphasis);
      break;
    case 'positive':
      stylesList.push(styles.ventasIndicatorValuePositive);
      break;
    case 'negative':
      stylesList.push(styles.ventasIndicatorValueNegative);
      break;
    case 'warning':
      stylesList.push(styles.ventasIndicatorValueWarning);
      break;
    default:
      break;
  }
  return stylesList;
}

/** Métrica plana para tableros Informes — sin borde/fondo propio (embebida en KpiStrip). */
export function ReportsMetricTile({
  label,
  labelVariant = 'kpi',
  value,
  sub,
  subAccent = false,
  subWarning = false,
  valueTone = 'default',
  valueEmphasis = false,
  warning = false,
  layout = 'default',
  onPress,
  disabled = false,
  accessibilityLabel,
  valueNumberOfLines,
  subNumberOfLines,
  style,
}: ReportsMetricTileProps) {
  const personal = layout === 'personal' || layout === 'personalWide';
  const emphasis = valueEmphasis || valueTone === 'emphasis';
  const containerStyle = [
    styles.reportsMetricTile,
    layoutStyle(layout),
    warning && styles.reportsMetricTileWarning,
    style,
  ];

  const labelEl =
    label != null ? (
      <Text
        style={labelVariant === 'breakdown' ? styles.ventasBreakdownMethodName : styles.ventasIndicatorLabel}
        numberOfLines={labelVariant === 'breakdown' ? 3 : undefined}
      >
        {label}
      </Text>
    ) : null;

  const valueEl = (
    <Text
      style={[
        ...valueToneStyle(valueTone, personal, emphasis),
        personal && layout === 'personalWide' && styles.personalKpiTileValueWide,
      ]}
      numberOfLines={valueNumberOfLines}
      adjustsFontSizeToFit={personal && valueNumberOfLines == null}
      minimumFontScale={personal ? 0.7 : undefined}
    >
      {value}
    </Text>
  );

  const subEl =
    sub != null ? (
      <Text
        style={[
          personal ? styles.personalKpiTileSub : styles.ventasIndicatorSub,
          subAccent && { color: ticketColors.gold },
          subWarning && styles.ventasIndicatorSubWarning,
          warning && personal && { color: colors.warning },
        ]}
        numberOfLines={subNumberOfLines}
      >
        {sub}
      </Text>
    ) : null;

  const content = (
    <>
      {labelEl}
      {valueEl}
      {subEl}
    </>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        style={containerStyle}
        onPress={onPress}
        disabled={disabled}
        activeOpacity={0.65}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityState={{ disabled }}
      >
        {content}
      </TouchableOpacity>
    );
  }

  return <View style={containerStyle}>{content}</View>;
}
